"use client";

import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { useSnackbar } from "notistack";
import { errorMessage } from "@/src/core/api/validation";
import {
  bookingApi,
  type IBookingPayload,
} from "@/src/shared/features/booking";
import { chatApi } from "@/src/shared/features/chat";

interface IBookingRequestTarget {
  hostId: number | string;
  guestId: number | string;
  /** An instant booking goes to its payment page; anything else to the host chat. */
  instant: boolean;
}

/** Creates a booking and opens where the guest continues. Resolves false when it failed. */
export function useBookingRequest() {
  const router = useAppRouter();
  const { enqueueSnackbar } = useSnackbar();

  return async (
    payload: IBookingPayload,
    { hostId, guestId, instant }: IBookingRequestTarget,
  ): Promise<boolean> => {
    let booking;
    try {
      booking = await bookingApi.create(payload);
    } catch (error) {
      enqueueSnackbar(
        errorMessage(error, "An error occurred while processing your booking"),
        { variant: "error" },
      );
      return false;
    }
    if (instant) {
      void router.push(`/booking/${booking.id}`);
      return true;
    }
    const convId =
      booking.conversation_id ??
      (await chatApi.findConversationId({
        host: hostId,
        guest: guestId,
        listing_id: payload.listing_id,
      }));
    if (convId) void router.push(`/chat/${convId}`);
    return true;
  };
}
